<script lang="ts" setup>
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  type ProfileNotificationRow,
  persistUserInboxToastsMuted,
  useUserInboxStore,
} from '@/stores/userInbox'
import {
  type InboxCategoryTab,
  inboxBellUnreadCount,
  inboxNotificationBody,
  inboxNotificationPrimaryAction,
  inboxNotificationTitle,
  inboxRowMatchesCategoryTab,
} from '@/utils/inboxNotificationDisplay'

const inbox = useUserInboxStore()
const { drawerOpen, items, loading } = storeToRefs(inbox)
const highlightedIds = ref<string[]>([])
const showClearAllConfirmDialog = ref(false)
const categoryTab = ref<InboxCategoryTab>('all')
const markUnreadOnOpenPending = ref(false)

const filteredItems = computed(() =>
  items.value.filter(n => inboxRowMatchesCategoryTab(n, categoryTab.value)),
)

const unreadBadgeAll = computed(() => inboxBellUnreadCount(items.value))

const unreadBadgeAction = computed(
  () =>
    items.value.filter(
      n => n.read_at == null && inboxRowMatchesCategoryTab(n, 'action'),
    ).length,
)

const unreadBadgeInformational = computed(() =>
  inboxBellUnreadCount(
    items.value.filter(
      n => n.read_at == null && inboxRowMatchesCategoryTab(n, 'informational'),
    ),
  ),
)

const clearAllDialogTitle = computed(() => {
  switch (categoryTab.value) {
  case 'action':
    return 'Clear all Action required notifications?'
  case 'informational':
    return 'Clear all Informational notifications?'
  default:
    return 'Clear entire notification inbox?'
  }
})

const clearAllDialogMessage = computed(() => {
  switch (categoryTab.value) {
  case 'action':
    return 'This permanently deletes every Action required notification for your account. Informational notifications are not removed.'
  case 'informational':
    return 'This permanently deletes every Informational notification for your account. Action required notifications are not removed.'
  default:
    return 'This permanently deletes every notification in this inbox for your account: both Action required and Informational.'
  }
})

const clearAllConfirmCopy = computed(() => {
  switch (categoryTab.value) {
  case 'action':
    return 'Clear Action required'
  case 'informational':
    return 'Clear Informational'
  default:
    return 'Clear entire inbox'
  }
})

function formatTime(iso: string): string {
  const m = moment(iso)
  const now = moment()
  if (m.isSame(now, 'day'))
    return `Today @ ${m.format('h:mma')}`
  if (m.isSame(now.clone().subtract(1, 'day'), 'day'))
    return `Yesterday @ ${m.format('h:mma')}`

  return m.format('MMM D @ h:mma')
}

function primaryLink(n: ProfileNotificationRow): { label: string; to: string } | null {
  return inboxNotificationPrimaryAction(n)
}

async function goPrimary(n: ProfileNotificationRow): Promise<void> {
  const link = primaryLink(n)
  if (!link)
    return
  inbox.closeDrawer()
  window.open(link.to, '_blank', 'noopener,noreferrer')
}

async function onDismiss(n: ProfileNotificationRow): Promise<void> {
  await inbox.remove(n.id)
}

async function onClearAll(): Promise<void> {
  showClearAllConfirmDialog.value = true
}

async function confirmClearAll(): Promise<void> {
  showClearAllConfirmDialog.value = false
  await inbox.clearAll(categoryTab.value)
}

function cancelClearAll(): void {
  showClearAllConfirmDialog.value = false
}

function isUnread(n: ProfileNotificationRow): boolean {
  return n.read_at == null || highlightedIds.value.includes(n.id)
}

function onMuteToggle(value: boolean | null): void {
  const muted = value === true

  // Handle stale/hydrated store instances where action methods may be missing at runtime.
  if (typeof (inbox as { setToastsMuted?: (next: boolean) => void }).setToastsMuted === 'function') {
    (inbox as { setToastsMuted: (next: boolean) => void }).setToastsMuted(muted)

    return
  }

  inbox.toastsMuted = muted
  persistUserInboxToastsMuted(muted)
}

/** Seeing a row in the open drawer counts as read (server `read_at`). */
watch(
  () => loading.value,
  async isLoading => {
    if (isLoading || !drawerOpen.value || !markUnreadOnOpenPending.value)
      return

    markUnreadOnOpenPending.value = false

    const unreadIds = inbox.items.filter(n => n.read_at == null).map(n => n.id)
    if (unreadIds.length === 0)
      return

    await inbox.markManyRead(unreadIds)
  },
  { flush: 'post' },
)

watch(
  () => drawerOpen.value,
  isOpen => {
    if (!isOpen) {
      highlightedIds.value = []
      markUnreadOnOpenPending.value = false

      return
    }

    markUnreadOnOpenPending.value = true
    highlightedIds.value = items.value
      .filter(n => n.read_at == null)
      .map(n => n.id)
  },
)

watch(
  () => items.value,
  rows => {
    if (!drawerOpen.value)
      return

    const merged = new Set(highlightedIds.value)
    for (const row of rows) {
      if (row.read_at == null)
        merged.add(row.id)
    }
    highlightedIds.value = Array.from(merged)
  },
  { deep: true },
)
</script>

<template>
  <div class="user-inbox-drawer-root">
    <VNavigationDrawer
      v-model="drawerOpen"
      location="end"
      temporary
      width="420"
      class="user-inbox-drawer border-s"
    >
      <div class="d-flex flex-column h-100 bg-surface">
        <div class="pa-4 pb-3 flex-shrink-0">
          <div class="d-flex align-center justify-space-between gap-3 mb-3 flex-wrap">
            <span class="text-h6 font-weight-bold text-high-emphasis">Notifications</span>
            <div class="d-flex align-center flex-shrink-0 flex-wrap">
              <div class="d-flex align-center mute-inline">
                <div class="mute-inline__switch">
                  <VSwitch
                    :model-value="inbox.toastsMuted"
                    color="primary"
                    density="compact"
                    hide-details
                    @update:model-value="onMuteToggle"
                  />
                </div>
                <span class="text-caption text-medium-emphasis mute-inline__label">Mute</span>
              </div>
              <VBtn
                variant="outlined"
                color="primary"
                size="small"
                class="text-none"
                :disabled="loading || inbox.items.length === 0"
                @click="onClearAll"
              >
                <VIcon start icon="mdi-delete-outline" size="18" />
                Clear all
              </VBtn>
            </div>
          </div>
          <div class="d-inline-flex mb-3">
            <VBtnToggle
              v-model="categoryTab"
              mandatory
              divided
              density="compact"
              variant="outlined"
              color="primary"
              rounded="0"
              class="inbox-category-toggle"
            >
              <VBtn
                value="all"
                class="inbox-category-tab"
              >
                ALL<template v-if="unreadBadgeAll > 0"><span class="inbox-tab-count text-medium-emphasis"> ({{ unreadBadgeAll }})</span></template>
              </VBtn>
              <VBtn
                value="action"
                class="inbox-category-tab"
              >
                ACTION REQUIRED<template v-if="unreadBadgeAction > 0"><span class="inbox-tab-count text-medium-emphasis"> ({{ unreadBadgeAction }})</span></template>
              </VBtn>
              <VBtn
                value="informational"
                class="inbox-category-tab"
              >
                INFORMATIONAL<template v-if="unreadBadgeInformational > 0"><span class="inbox-tab-count text-medium-emphasis"> ({{ unreadBadgeInformational }})</span></template>
              </VBtn>
            </VBtnToggle>
          </div>
        </div>

        <VDivider />

        <div class="flex-grow-1 overflow-y-auto">
          <div v-if="loading && items.length === 0" class="pa-8 text-center text-medium-emphasis">
            <VProgressCircular indeterminate color="primary" size="32" />
          </div>
          <div v-else-if="items.length === 0" class="pa-8 text-center text-medium-emphasis text-body-2">
            No notifications yet
          </div>
          <div
            v-else-if="filteredItems.length === 0"
            class="pa-8 text-center text-medium-emphasis text-body-2"
          >
            No notifications in this category
          </div>
          <template v-else>
            <div
              v-for="n in filteredItems"
              :key="n.id"
              class="inbox-row pa-4 border-b"
              :class="{ 'inbox-row--unread': isUnread(n) }"
            >
              <div class="inbox-row__row d-flex gap-3">
                <div class="inbox-row__icon flex-shrink-0">
                  <VIcon class="inbox-row__bell" icon="mdi-bell-outline" size="16" />
                </div>
                <div class="min-w-0 flex-grow-1">
                  <div class="text-body-1 font-weight-bold text-high-emphasis rb-mask">
                    {{ inboxNotificationTitle(n) }}
                  </div>
                  <div class="text-caption text-medium-emphasis mt-1">
                    {{ formatTime(n.created_at) }}
                  </div>
                  <div class="text-body-2 text-medium-emphasis mt-2 inbox-row__body rb-mask">
                    {{ inboxNotificationBody(n) }}
                  </div>
                  <div class="d-flex flex-wrap gap-x-4 gap-y-1 mt-3">
                    <a
                      v-if="primaryLink(n)"
                      href="#"
                      class="text-primary text-decoration-underline text-body-2 font-weight-bold"
                      @click.prevent="goPrimary(n)"
                    >{{ primaryLink(n)?.label }}</a>
                    <a
                      href="#"
                      class="text-decoration-underline text-body-2 text-medium-emphasis"
                      @click.prevent="onDismiss(n)"
                    >Dismiss</a>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </VNavigationDrawer>

    <ConfirmDialog
      :show-dialog="showClearAllConfirmDialog"
      :title="clearAllDialogTitle"
      :message="clearAllDialogMessage"
      :confirm-copy="clearAllConfirmCopy"
      cancel-copy="Cancel"
      confirm-color="error"
      :loading="loading"
      @confirm="confirmClearAll"
      @cancel="cancelClearAll"
    />
  </div>
</template>

<!-- Unscoped: drawer emits multiple roots (nav + scrim); must sit above layout navbar (z-index 2001) -->
<style lang="scss">
.user-inbox-drawer-root {
  .v-navigation-drawer--temporary.v-navigation-drawer--active {
    z-index: 2501 !important;
  }

  .v-navigation-drawer__scrim {
    z-index: 2500 !important;
  }
}
</style>

<style lang="scss" scoped>
.user-inbox-drawer {
  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
  }
}

.inbox-row__row {
  align-items: flex-start;
}

.inbox-row__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  inline-size: 24px;
  block-size: 24px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  box-sizing: border-box;
  border: 1px solid rgb(var(--v-theme-on-primary));
}

.inbox-row__bell {
  color: rgb(var(--v-theme-on-primary)) !important;
}

.inbox-row__body {
  white-space: pre-wrap;
  word-break: break-word;
}

.inbox-row--unread {
  background: rgba(var(--v-theme-primary), 0.06);
}

.border-b {
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Compact switch in header: toggle left, label right */
.mute-inline {
  display: flex;
  align-items: center;
  min-block-size: 28px;
  column-gap: 10px;
  padding-inline-end: 16px;
}

.mute-inline__label {
  flex-shrink: 0;
}

.mute-inline__switch {
  flex-shrink: 0;
  transform: scale(0.78);
  transform-origin: center;
  margin: -6px -4px -6px -6px;
}

.mute-inline__switch :deep(.v-switch) {
  margin-inline-end: 0;
}

.inbox-category-toggle {
  border-radius: 0;

  :deep(.v-btn.inbox-category-tab) {
    border-radius: 0;
    flex: 0 0 auto;
    min-inline-size: unset;
    min-width: unset;
    padding-block: 6px;
    padding-inline: 10px;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: 1.2;
    white-space: nowrap;
  }
}

.inbox-tab-count {
  font-size: 0.625rem;
  font-weight: 500;
  letter-spacing: normal;
}
</style>
